import { useId } from "react";
import type { FlowLayout } from "./layout";

export type Placement = { x: number; y: number; scale: number };

const MAX_W = 1152;

/** Centre a laid-out flow horizontally on the stage and vertically around centreY; shrink if it can't fit. */
export function placeFlow(layout: FlowLayout, centreY: number, maxH: number): Placement {
  const scale = Math.min(1, MAX_W / Math.max(1, layout.width), maxH / Math.max(1, layout.height));
  return {
    x: Math.round((1280 - layout.width * scale) / 2),
    y: Math.round(centreY - (layout.height * scale) / 2),
    scale,
  };
}

/** A node's centre in stage coordinates: the camera's transform-origin when zooming into it. */
export function nodeCentre(layout: FlowLayout, at: Placement, id: string): { x: number; y: number } {
  const b = layout.nodes.get(id);
  if (!b) return { x: 640, y: 360 };
  return { x: at.x + (b.x + b.w / 2) * at.scale, y: at.y + (b.y + b.h / 2) * at.scale };
}

type Node = { id: string; label: string; caption?: string };

/** Rough width of a label at the full node size (22px Geist semibold): enough to tell one line from two. */
function labelWidth(label: string): number {
  return label.length * 12;
}

type Props = {
  layout: FlowLayout;
  nodes: Node[];
  at: Placement;
  size: "top" | "sub";
  /** The yellow node: next to zoom into (top level) or the sub-flow's emphasis. */
  hotId?: string;
  /** Show "CLICK TO ZOOM IN" over the hot node. */
  hint?: boolean;
  zoomable?: Set<string>;
  onZoom?: (id: string) => void;
};

export function FlowDiagram({ layout, nodes, at, size, hotId, hint, zoomable, onZoom }: Props) {
  const uid = useId().replace(/:/g, "");
  const hot = hotId ? layout.nodes.get(hotId) : undefined;
  return (
    <div
      className="abs"
      style={{
        left: at.x,
        top: at.y,
        width: layout.width,
        height: layout.height,
        transform: at.scale === 1 ? undefined : `scale(${at.scale})`,
        transformOrigin: "0 0",
      }}
    >
      <svg className="flow-edges" width={layout.width} height={layout.height} aria-hidden="true">
        <defs>
          {(["forward", "loop"] as const).map((k) => (
            <marker
              key={k}
              id={`${uid}-${k}`}
              orient="auto"
              markerWidth="5"
              markerHeight="5"
              refX="2"
              refY="2"
              overflow="visible"
            >
              <path d="M0 0 L4 2 L0 4 Z" className={`flow-arrow-${k}`} />
            </marker>
          ))}
        </defs>
        {layout.edges.map((e, i) => (
          <path key={i} d={e.d} className={`flow-edge-${e.kind}`} markerEnd={`url(#${uid}-${e.kind})`} />
        ))}
      </svg>
      {layout.edges.map(
        (e, i) =>
          e.label && (
            <div key={i} className="flow-label" data-kind={e.kind} style={{ left: e.label.x, top: e.label.y }}>
              {e.label.text}
            </div>
          ),
      )}
      {nodes.map((n) => {
        const b = layout.nodes.get(n.id);
        if (!b) return null;
        const style = { left: b.x, top: b.y, width: b.w, height: b.h };
        const body = (
          <span>
            <b className="flow-node-label">{n.label}</b>
            {n.caption && (
              <>
                <br />
                {n.caption}
              </>
            )}
          </span>
        );
        const isHot = n.id === hotId;
        const long = labelWidth(n.label) > b.w - 32;
        return zoomable?.has(n.id) && onZoom ? (
          <button
            key={n.id}
            type="button"
            className="flow-node"
            data-size={size}
            data-hot={isHot}
            data-long={long}
            data-node={n.id}
            style={style}
            onClick={() => onZoom(n.id)}
            aria-label={`Zoom into ${n.label}`}
          >
            {body}
          </button>
        ) : (
          <div
            key={n.id}
            className="flow-node"
            data-size={size}
            data-hot={isHot}
            data-long={long}
            data-node={n.id}
            style={style}
          >
            {body}
          </div>
        );
      })}
      {hint && hot && (
        <div className="flow-hint" style={{ left: hot.x, top: hot.y - 30, width: hot.w }}>
          CLICK TO ZOOM IN
        </div>
      )}
    </div>
  );
}
